import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity'
import { Action, createReducer, on } from '@ngrx/store'

import { GridSwitchEnum } from '@galabid/gb-dashboard/shared/feature'
import {
  clearSearchHistoryAction,
  getAddSearchHistoryAction,
} from 'libs/gb-dashboard/shared/data-access-search-history/src/lib/+state/search-history.actions'
import { AuctionBlockListEntity, ProfilesEntity } from './profiles.models'
import * as UsersActions from './users.actions'
import { usersEntityName } from './users.models'

export const USERS_FEATURE_KEY = 'users'

export interface UsersState extends EntityState<ProfilesEntity> {
  selectedId?: string | number
  loaded: boolean
  count: number
  dataView: GridSwitchEnum
  searchHistory: string[]
  editCreateLoaded: boolean
  saveAndSendInviteLoaded: boolean
  changePasswordLoaded: boolean
  messageLoaded: boolean
  inviteLoaded: boolean

  createBlockLoaded: boolean

  blocksLoaded: boolean
  blocksCount: number
  blocks: AuctionBlockListEntity[]
}

export interface UsersPartialState {
  readonly [USERS_FEATURE_KEY]: UsersState
}

export const usersAdapter: EntityAdapter<ProfilesEntity> = createEntityAdapter<ProfilesEntity>({
  selectId: (user: ProfilesEntity) => user.uuid,
})

export const initialUsersState: UsersState = usersAdapter.getInitialState({
  loaded: true,
  count: 0,
  dataView: GridSwitchEnum.Table,
  searchHistory: [],
  editCreateLoaded: true,
  saveAndSendInviteLoaded: true,
  changePasswordLoaded: true,
  messageLoaded: true,
  inviteLoaded: true,

  createBlockLoaded: true,

  blocksLoaded: true,
  blocksCount: 0,
  blocks: [],
})

const reducer = createReducer(
  initialUsersState,
  on(UsersActions.loadUsers, (state) => ({
    ...state,
    loaded: false,
  })),
  on(UsersActions.loadUsersSuccess, (state, action) =>
    usersAdapter.upsertMany(action.users, {
      ...state,
      loaded: true,
      count: action.count,
    }),
  ),
  on(UsersActions.setUsers, (state, action) =>
    usersAdapter.setAll(action.users, {
      ...state,
      loaded: true,
      count: action.count,
    }),
  ),
  on(UsersActions.selectUser, UsersActions.openEditUserDialog, (state, { uuid }) => ({
    ...state,
    selectedId: uuid,
  })),
  on(UsersActions.clearSelectedUser, (state) => ({
    ...state,
    selectedId: undefined,
  })),
  on(UsersActions.updateUserSuccess, UsersActions.updateUserForRealtime, (state, profile) =>
    usersAdapter.updateOne(profile.update, state),
  ),
  on(UsersActions.deleteUserSuccess, (state, profile) =>
    usersAdapter.removeOne(profile.uuid, { ...state, count: state.count - 1 }),
  ),
  on(UsersActions.displayGrid, (state) => ({
    ...state,
    dataView: GridSwitchEnum.Grid,
  })),
  on(UsersActions.displayTable, (state) => ({
    ...state,
    dataView: GridSwitchEnum.Table,
  })),
  on(UsersActions.getProfileSuccess, (state, { profile }) => usersAdapter.upsertOne(profile, state)),
  on(getAddSearchHistoryAction(usersEntityName), (state, action) => ({
    ...state,
    searchHistory: [...state.searchHistory, action.searchTerm],
  })),
  on(clearSearchHistoryAction(usersEntityName), (state) => ({
    ...state,
    searchHistory: initialUsersState.searchHistory,
  })),
  on(UsersActions.createUser, UsersActions.updateSelectedUser, UsersActions.assignPaddleNumber, (state) => ({
    ...state,
    editCreateLoaded: false,
  })),
  on(
    UsersActions.createUserSuccess,
    UsersActions.createUserFailure,
    UsersActions.updateUserSuccess,
    UsersActions.updateUserFailure,
    (state) => ({
      ...state,
      editCreateLoaded: true,
      saveAndSendInviteLoaded: true,
    }),
  ),
  on(UsersActions.updateAndSendInvite, (state) => ({
    ...state,
    saveAndSendInviteLoaded: false,
  })),
  on(UsersActions.changePassword, (state) => ({
    ...state,
    changePasswordLoaded: false,
  })),
  on(UsersActions.changePasswordSuccess, UsersActions.changePasswordFailure, (state) => ({
    ...state,
    changePasswordLoaded: true,
  })),

  on(UsersActions.messageUser, (state) => ({
    ...state,
    messageLoaded: false,
  })),
  on(UsersActions.messageUserSuccess, UsersActions.messageUserFailure, (state) => ({
    ...state,
    messageLoaded: true,
  })),
  on(UsersActions.inviteUsersEmail, UsersActions.inviteUsersPhoneNumber, (state) => ({
    ...state,
    inviteLoaded: false,
  })),
  on(
    UsersActions.inviteUsersEmailSuccess,
    UsersActions.inviteUsersEmailFailure,
    UsersActions.inviteUsersPhoneSuccess,
    UsersActions.inviteUsersPhoneFailure,
    (state) => ({
      ...state,
      inviteLoaded: true,
    }),
  ),
  on(UsersActions.inviteUsersEmailSuccess, (state, action) =>
    usersAdapter.upsertMany(action.users, {
      ...state,
      count: state.count + action.users.length,
    }),
  ),
  on(UsersActions.clearUsers, (state) => ({
    ...initialUsersState,
    dataView: state.dataView,
  })),
  on(UsersActions.clearBlocks, (state) => ({
    ...state,
    blocks: [],
    blocksCount: 0,
  })),
  on(UsersActions.loadBlocks, (state) => ({
    ...state,
    blocksLoaded: false,
  })),
  on(UsersActions.loadBlocksSuccess, (state, action) => ({
    ...state,
    blocksLoaded: true,
    blocksCount: action.count,
    blocks: [...state.blocks, ...(action.blocks ?? [])],
  })),
  on(UsersActions.loadBlocksFailure, (state) => ({
    ...state,
    blocksLoaded: true,
  })),
  on(UsersActions.createBlock, UsersActions.deleteUserBlock, (state) => ({
    ...state,
    createBlockLoaded: false,
  })),
  on(UsersActions.createBlockSuccess, (state, action) => ({
    ...state,
    createBlockLoaded: true,
    blocksCount: state.blocksCount + 1,
    blocks: [action.block, ...state.blocks],
  })),
  on(
    UsersActions.createBlockFailure,
    UsersActions.deleteUserBlockSuccess,
    UsersActions.deleteUserBlockFailure,
    (state) => ({
      ...state,
      createBlockLoaded: true,
    }),
  ),
  on(UsersActions.updateBlock, (state) => ({
    ...state,
    createBlockLoaded: false,
  })),
  on(UsersActions.updateBlockSuccess, (state, action) => ({
    ...state,
    createBlockLoaded: true,
    blocks: state.blocks.map((b) => (b.id === action.block.id ? action.block : b)),
  })),
  on(UsersActions.updateBlockFailure, (state) => ({
    ...state,
    createBlockLoaded: true,
  })),
  on(UsersActions.deleteBlockSuccess, (state, action) => ({
    ...state,
    blocksCount: Math.max(0, state.blocksCount - 1),
    blocks: state.blocks.filter((b) => b.id !== action.id),
  })),
  on(UsersActions.importUsersSuccess, (state, action) =>
    usersAdapter.upsertMany(action.response.users, {
      ...state,
      count: state.count + action.response.users_count,
    }),
  ),
)

export function usersReducer(state: UsersState | undefined, action: Action) {
  return reducer(state, action)
}
